// Sensitive-action verification (core's 428 `verification_required`).
//
// One axios response interceptor watches every request made through the global
// axios instance (helpers/api.js is a thin wrapper over it). When core answers
// 428 with `status: "verification_required"`, the interceptor hands the
// `verification` payload to the modal host (components/_modals/Auth/
// VerificationModal, mounted once in App.js), waits for the person to prove it
// is them, and replays the original request with the grant in
// `X-Verification-Grant`. Callers never learn a verification happened: their
// promise resolves with the retried response.
//
// Flows stack. A 428 raised by a call made from INSIDE a flow (the enrollment
// request-code answering `required` for scope `user.phone`) opens a second flow
// on top; the host renders only the top of the stack and the previous flow
// continues once the nested one resolves.
//
// Public API:
//   registerVerificationInterceptor()        once, where axios defaults are set
//   registerVerificationHost({ start })      by the modal host on mount → unregister fn
//   runVerificationFlow(verification, config) → Promise<{ grant, outcome }>
//     grant:   the 64-char grant, or null
//     outcome: 'verified' | 'cancelled' | 'locked'
//   verificationRequest(method, url, body, config) direct axios call that RETURNS
//     HTTP errors (api.js convention) so flows can branch on the status
//   readLastMethod() / storeLastMethod(channel) / clearLastMethod()

import axios from "axios";

export const VERIFICATION_STATUS = "verification_required";
export const GRANT_HEADER = "X-Verification-Grant";
export const LAST_METHOD_STORAGE_KEY = "verification:last-method";

// A retried request that still comes back `grant_rejected` reopens the flow.
// After this many grant cycles the retry is marked `_skipVerification`, so the
// next 428 reaches the caller untouched instead of looping.
const MAX_GRANT_CYCLES = 2;

let interceptorRegistered = false;
let host = null;
let flowSequence = 0;

// ── Host registry ───────────────────────────────────────────────────────────

/**
 * The modal host registers itself here. `handler.start(flow)` receives
 * `{ id, verification, config, resolve }`; the host must eventually call
 * `flow.resolve({ grant, outcome })`. Returns the unregister function.
 */
export function registerVerificationHost(handler) {
    host = handler;
    return () => {
        if (host === handler) host = null;
    };
}

function normalizeResult(result) {
    if (typeof result === "string") return { grant: result, outcome: "verified" };
    if (result?.grant) return { grant: result.grant, outcome: "verified" };
    return { grant: null, outcome: result?.outcome === "locked" ? "locked" : "cancelled" };
}

/**
 * Opens a verification flow for the given 428 payload. Resolves once the person
 * verified (grant), closed the modal (cancelled) or was told they are locked.
 * Without a mounted host it resolves `cancelled` right away so the request
 * never hangs.
 */
export function runVerificationFlow(verification, config) {
    if (!host) return Promise.resolve({ grant: null, outcome: "cancelled" });

    return new Promise((resolve) => {
        let settled = false;
        host.start({
            id: ++flowSequence,
            verification: verification || {},
            config: config || {},
            resolve(result) {
                if (settled) return;
                settled = true;
                resolve(normalizeResult(result));
            },
        });
    });
}

// ── Interceptor ─────────────────────────────────────────────────────────────

export function isVerificationError(error) {
    return (
        error?.response?.status === 428 &&
        error?.response?.data?.status === VERIFICATION_STATUS
    );
}

export function readVerification(error) {
    return error?.response?.data?.verification || null;
}

// axios 1.x keeps request headers in an AxiosHeaders instance; a plain object
// is what we spread the grant into before replaying.
function toPlainHeaders(headers) {
    if (!headers) return {};
    if (typeof headers.toJSON === "function") return headers.toJSON();
    return { ...headers };
}

export function registerVerificationInterceptor() {
    if (interceptorRegistered) return;
    interceptorRegistered = true;

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const config = error?.config;

            // A request whose signal is already aborted belongs to a flow that
            // was closed while the call was in flight (the flow aborts its
            // controller on finish). Its 428 must not open a flow nobody asked
            // for — hand the error back untouched.
            if (
                !config ||
                config._skipVerification ||
                config.signal?.aborted ||
                !isVerificationError(error)
            ) {
                return Promise.reject(error);
            }

            const verification = readVerification(error) || {};
            const reason = verification.reason || "required";

            // Only `required` opens a flow, plus a `locked` answered by the
            // middleware on the action itself (so the person learns why). A
            // `locked` answered to a flow's own request-code call is handled
            // in place by that flow; `invalid` / `expired` only come from the
            // verify endpoint, which is skip-flagged anyway.
            const opensFlow =
                reason === "required" ||
                (reason === "locked" && !config._verificationRequest);

            if (!opensFlow) return Promise.reject(error);

            const result = await runVerificationFlow(verification, config);

            if (!result.grant) {
                if (result.outcome === "locked") error.verificationLocked = true;
                else error.verificationCancelled = true;
                return Promise.reject(error);
            }

            const cycles = (config._verificationCycles || 0) + 1;

            return axios.request({
                ...config,
                _verificationCycles: cycles,
                _skipVerification: cycles >= MAX_GRANT_CYCLES,
                headers: {
                    ...toPlainHeaders(config.headers),
                    [GRANT_HEADER]: result.grant,
                },
            });
        },
    );
}

// ── Requests made by the flow itself ────────────────────────────────────────

/**
 * Direct axios call for the verification endpoints. Mirrors helpers/api.js:
 * an HTTP error is RETURNED (the AxiosError, with `.response`), never thrown,
 * so the flow branches on `response.status` either way. Pass
 * `{ _skipVerification: true }` for the verify call and
 * `{ _verificationRequest: true }` for request-code (which must stay
 * interceptable so the phone-enrollment nesting works).
 */
export async function verificationRequest(method, url, body, config = {}) {
    try {
        return await axios.request({ method, url, data: body, ...config });
    } catch (e) {
        // Same session guard as helpers/api.js: a dead token signs the person
        // out instead of leaving them retrying inside the modal.
        if (e?.response?.status === 401 && window.signed) window.logout?.();
        return e;
    }
}

export function isSuccessResponse(response) {
    const status = response?.status;
    return !!response && !response.isAxiosError && status >= 200 && status < 300;
}

// ── Last method (per-browser convenience) ───────────────────────────────────

export function readLastMethod() {
    try {
        return localStorage.getItem(LAST_METHOD_STORAGE_KEY) || null;
    } catch (e) {
        return null;
    }
}

export function storeLastMethod(channel) {
    try {
        if (channel) localStorage.setItem(LAST_METHOD_STORAGE_KEY, channel);
    } catch (e) {
        /* storage unavailable — the method step simply shows next time */
    }
}

export function clearLastMethod() {
    try {
        localStorage.removeItem(LAST_METHOD_STORAGE_KEY);
    } catch (e) {
        /* nothing to clear */
    }
}
